import {
  type AppointmentCustomer,
  type Contact,
  type Customer,
  type CustomerConsentPayload,
  type DraftOrderCustomer,
  type Order,
  type QuotationCustomer,
} from "@repo/api";
import { t } from "@repo/translation";
import { getFullName, maskName, removeEmptyValuesInObject } from "@repo/utils";
import isEmpty from "lodash/isEmpty";

export type CustomerSource =
  | "CUSTOMER_PROFILE"
  | "OPPORTUNITY"
  | "BOKA"
  | "ORDER"
  | "DRAFT_ORDER"
  | "QUOTATION"
  | "CONSENT";

export const getCustomerFromConsent = (
  consent?: CustomerConsentPayload
): Customer => ({
  firstName: consent?.firstName ?? "",
  lastName: consent?.lastName ?? "",
  customerType: consent?.customerType ?? "Individual",
  email: consent?.email ?? "",
  partyId: consent?.partyId ?? "",
  phoneNumber: consent?.phone ?? "",
  preferredContactType: consent?.preferredContactType ?? "",
  postalCode: "",
  city: "",
  streetAddress1: "",
});

export const getCutomerSourceLabel = (customerSource?: CustomerSource) => {
  if (customerSource === "BOKA") {
    return t("prompt.provided_by_boka_source");
  } else if (customerSource === "ORDER") {
    return t("prompt.provided_by_order_source");
  } else if (customerSource === "DRAFT_ORDER") {
    return t("prompt.provided_by_draft_order_source");
  } else if (customerSource === "QUOTATION") {
    return t("prompt.provided_by_quotation_source");
  } else {
    return undefined;
  }
};

export const mapCustomerDetailsFromBoka = (
  appointmentCustomer?: AppointmentCustomer
): Partial<Customer> => {
  return {
    firstName: appointmentCustomer?.firstName,
    lastName: appointmentCustomer?.lastName,
    phoneNumber: appointmentCustomer?.phoneNumber,
    email: appointmentCustomer?.email,
  };
};

export const mapCustomerFromOrder = (
  order: Order,
  isAppointmentOrder: boolean
): Partial<Customer> => {
  const orderCustomer = isAppointmentOrder
    ? order.billingContact
    : order.deliveryContact;
  return {
    firstName: orderCustomer?.customerContactDetails?.firstName,
    lastName: orderCustomer?.customerContactDetails?.lastName,
    customerType:
      (orderCustomer?.customerContactDetails
        ?.customerType as Customer["customerType"]) ?? "Individual",
    organizationName: orderCustomer?.customerContactDetails?.businessName,
    phoneNumber:
      orderCustomer?.customerContactDetails?.contactTypeValues?.MOBILE,
    email: orderCustomer?.customerContactDetails?.contactTypeValues?.EMAIL,
    postalCode: orderCustomer?.address?.zipCode,
    city: orderCustomer?.address?.cityName,
    streetAddress1: orderCustomer?.address?.addressLine1,
  };
};

export const mapCustomerFromDraftOrder = (
  draftOrderCustomer: DraftOrderCustomer
): Partial<Customer> => {
  return {
    firstName: draftOrderCustomer.firstName,
    lastName: draftOrderCustomer.lastName,
    email: draftOrderCustomer.email,
    postalCode: draftOrderCustomer.zipCode,
    phoneNumber: draftOrderCustomer.mobilePhone,
    city: draftOrderCustomer.city,
    streetAddress1: draftOrderCustomer.addressLine1,
    streetAddress2: draftOrderCustomer.addressLine2,
    streetAddress3: draftOrderCustomer.addressLine3,
    state: draftOrderCustomer.stateCode,
    language: draftOrderCustomer.language,
    country: draftOrderCustomer.countryCode,
    customerLocale: draftOrderCustomer.preferredLocale,
    familyNumber:
      draftOrderCustomer.ikeaFamilyNumber || draftOrderCustomer.businessCardNo,
    partyId: draftOrderCustomer.partyId,
    organizationName: draftOrderCustomer.isBusinessCustomer
      ? draftOrderCustomer.businessName || draftOrderCustomer.fullName
      : undefined,
  };
};

export const mapCustomerFromQuotation = (
  quotationCustomer: QuotationCustomer
): Partial<Customer> => {
  return {
    firstName: quotationCustomer?.firstName,
    lastName: quotationCustomer?.lastName,
    email: quotationCustomer?.email,
    phoneNumber: quotationCustomer?.mobilePhone,
    postalCode: quotationCustomer?.zipCode,
    city: quotationCustomer?.city,
    streetAddress1: quotationCustomer?.addressLine1,
    state: quotationCustomer?.stateCode,
    country: quotationCustomer?.countryCode,
    language: quotationCustomer?.language,
    customerLocale: quotationCustomer?.preferredLocale,
    partyId: quotationCustomer?.partyId,
    organizationName: quotationCustomer?.isBusinessCustomer
      ? quotationCustomer?.businessName ||
        `${quotationCustomer?.firstName} ${quotationCustomer?.lastName}`
      : undefined,
    customerType: quotationCustomer?.isBusinessCustomer
      ? "Business"
      : "Individual",
  };
};

export const getCustomerEmail = (
  customer: Customer,
  appointmentCustomer?: AppointmentCustomer,
  orderCustomer?: Contact,
  draftOrderCustomer?: DraftOrderCustomer,
  appointmentOrderCustomer?: Contact,
  quotationCustomer?: QuotationCustomer
): string =>
  customer.email ||
  appointmentCustomer?.email ||
  orderCustomer?.customerContactDetails.contactTypeValues?.EMAIL ||
  draftOrderCustomer?.email ||
  appointmentOrderCustomer?.customerContactDetails?.contactTypeValues?.EMAIL ||
  quotationCustomer?.email ||
  "";

export const getCustomerPhone = (
  customer: Customer,
  appointmentCustomer?: AppointmentCustomer,
  orderCustomer?: Contact,
  draftOrderCustomer?: DraftOrderCustomer,
  appointmentOrderCustomer?: Contact,
  quotationCustomer?: QuotationCustomer
): string =>
  customer.phoneNumber ||
  appointmentCustomer?.phoneNumber ||
  orderCustomer?.customerContactDetails?.contactTypeValues?.MOBILE ||
  draftOrderCustomer?.mobilePhone ||
  appointmentOrderCustomer?.customerContactDetails?.contactTypeValues?.MOBILE ||
  quotationCustomer?.mobilePhone ||
  "";

export const getCustomerAddress = (
  customer: Customer,
  orderCustomer?: Contact,
  draftOrderCustomer?: DraftOrderCustomer,
  appointmentOrderCustomer?: Contact,
  quotationCustomer?: QuotationCustomer
): {
  postalCode?: string;
  city?: string;
  state?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  streetAddress3?: string;
} => {
  const customerAddress = removeEmptyValuesInObject({
    postalCode: customer.postalCode,
    city: customer.city,
    state: customer.state,
    streetAddress1: customer.streetAddress1,
  });

  const orderAddress = removeEmptyValuesInObject({
    postalCode: orderCustomer?.address?.zipCode,
    city: orderCustomer?.address?.cityName,
    streetAddress1: orderCustomer?.address?.addressLine1,
  });

  const draftOrderAddress = removeEmptyValuesInObject({
    postalCode: draftOrderCustomer?.zipCode,
    city: draftOrderCustomer?.city,
    state: draftOrderCustomer?.stateCode,
    streetAddress1: draftOrderCustomer?.addressLine1,
    streetAddress2: draftOrderCustomer?.addressLine2,
    streetAddress3: draftOrderCustomer?.addressLine3,
  });

  // // take customer address from appointment order if addresses from other sources are missing
  const appointmentOrderAddress = removeEmptyValuesInObject({
    postalCode: appointmentOrderCustomer?.address?.zipCode,
    city: appointmentOrderCustomer?.address?.cityName,
    streetAddress1: appointmentOrderCustomer?.address?.addressLine1,
  });

  const quotationAddress = removeEmptyValuesInObject({
    postalCode: quotationCustomer?.zipCode,
    city: quotationCustomer?.city,
    state: quotationCustomer?.stateCode,
    streetAddress1: quotationCustomer?.addressLine1,
  });

  if (!isEmpty(customerAddress)) {
    return customerAddress;
  }
  if (!isEmpty(orderAddress)) {
    return orderAddress;
  }
  if (!isEmpty(draftOrderAddress)) {
    return draftOrderAddress;
  }
  if (!isEmpty(appointmentOrderAddress)) {
    return appointmentOrderAddress;
  }

  if (!isEmpty(quotationAddress)) {
    return quotationAddress;
  }

  return {};
};

export const getCustomerLanguage = (locale: string) =>
  locale.slice(0, 2).toLowerCase();

export const getFormattedAddress = (
  customer?: Customer,
  useAddressFormat?: boolean,
  hideAddress?: boolean
) => {
  const customerAddress = hideAddress
    ? ["*****"]
    : [
        customer?.streetAddress1,
        customer?.streetAddress2,
        customer?.streetAddress3,
      ];
  return useAddressFormat
    ? [
        ...customerAddress,
        customer?.city,
        customer?.state,
        customer?.postalCode,
      ]
    : [...customerAddress, customer?.postalCode, customer?.city];
};

export const getCustomerName = (
  customer: {
    firstName?: string;
    lastName?: string;
    customerType?: "Individual" | "Business";
    organizationName?: string;
  },
  reverse?: boolean,
  mask?: boolean
) => {
  if (!customer) {
    return null;
  }

  const fullName = getFullName(
    customer?.firstName,
    customer?.lastName,
    reverse,
    mask
  );

  if (customer?.customerType === "Individual") {
    return fullName;
  }
  if (customer?.customerType === "Business") {
    return mask
      ? maskName(customer.organizationName || fullName)
      : customer.organizationName || fullName;
  }
};

export const maskProjectTitle = (
  title: string,
  customerName?: string,
  organizationName?: string
) => {
  const cleanTitle = title.replace(/\s+/g, " ").trim();

  const namesToMask = [customerName, organizationName]
    .filter((name): name is string => Boolean(name?.trim()))
    .map((name) => name.replace(/\s+/g, " ").trim());

  if (!namesToMask.length) {
    return cleanTitle;
  }

  // Escape special characters
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  return namesToMask.reduce((maskedTitle, name) => {
    const escapedName = escapeRegex(name);

    // If the name has multiple words, replace spaces
    const namePatternString = escapedName.includes(" ")
      ? escapedName.split(/\s+/).join("\\s+")
      : escapedName;

    const namePattern = new RegExp(namePatternString, "i");

    return maskedTitle.replace(namePattern, maskName(name));
  }, cleanTitle);
};
